/**
 * Request status lifecycle (spec §4, FR-19).
 *
 *   New → Acknowledged → In Progress → Resolved → Closed
 * plus side/terminal states: Duplicate, Invalid/Rejected.
 *
 * The status value is part of GSI1PK (ADR-0002), so a status change re-buckets the item in
 * the staff-queue index. Keep these values URL/key-safe (no spaces): use the enum values below.
 */
export const REQUEST_STATUSES = [
  "New",
  "Acknowledged",
  "InProgress",
  "Resolved",
  "Closed",
  "Duplicate",
  "Invalid",
] as const;

export type RequestStatus = (typeof REQUEST_STATUSES)[number];

/** Human-readable labels for display surfaces (web/notifications). */
export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  New: "New",
  Acknowledged: "Acknowledged",
  InProgress: "In Progress",
  Resolved: "Resolved",
  Closed: "Closed",
  Duplicate: "Duplicate",
  Invalid: "Invalid / Rejected",
};

/**
 * Allowed status transitions (spec §4).
 *
 *   New → Acknowledged → InProgress → Resolved → Closed
 *
 * `Duplicate` and `Invalid` are side/terminal states reachable from any *active* (non-terminal)
 * state. `Closed`, `Duplicate`, and `Invalid` are terminal (no outbound transitions).
 * A transition to the same status is not allowed (a status change must actually change state).
 */
const TRANSITIONS: Record<RequestStatus, readonly RequestStatus[]> = {
  New: ["Acknowledged", "InProgress", "Duplicate", "Invalid"],
  Acknowledged: ["InProgress", "Resolved", "Duplicate", "Invalid"],
  InProgress: ["Resolved", "Acknowledged", "Duplicate", "Invalid"],
  Resolved: ["Closed", "InProgress"],
  Closed: [],
  Duplicate: [],
  Invalid: [],
};

/** True iff moving a request from `from` to `to` is a permitted transition (spec §4). */
export function canTransition(from: RequestStatus, to: RequestStatus): boolean {
  if (from === to) return false;
  return TRANSITIONS[from].includes(to);
}
